// src/components/pickers/ChannelPicker.tsx
// Reusable across every page that takes a channel ID (design
// confirmed 2026-07-15): either pick from a live dropdown of the
// guild's real channels, shown as "Name (ID)", or fall back to typing
// a raw ID manually - the manual path still exists, since the cache
// could be briefly incomplete (e.g. a channel created seconds ago) or
// simply not what someone wants to use.
import { useState } from 'react';
import { useCachedChannels } from './useGuildStructure';

interface ChannelPickerProps {
  guildId: string | null;
  value: string;
  onChange: (channelId: string) => void;
  placeholder?: string;
}

export function ChannelPicker({ guildId, value, onChange, placeholder }: ChannelPickerProps) {
  const { data: channels, isLoading } = useCachedChannels(guildId);
  const hasChannels = !!channels && channels.length > 0;

  // Manual mode by default if the cache has nothing yet, or if the
  // current value doesn't match any cached channel (e.g. it was set
  // before the cache existed, or the channel's since been deleted) -
  // otherwise a saved value would silently appear blank in the dropdown.
  const matchesCache = hasChannels && channels.some((c) => c.channel_id === value);
  const [manualMode, setManualMode] = useState(!hasChannels || (!!value && !matchesCache));

  if (manualMode || !hasChannels) {
    return (
      <div className="channel-picker">
        <input
          className="input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder={placeholder ?? 'Channel ID'}
        />
        {hasChannels && (
          <button type="button" className="channel-picker__toggle" onClick={() => setManualMode(false)}>
            Choose from list instead
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="channel-picker">
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading}
      >
        <option value="">— Select a channel —</option>
        {channels.map((c) => (
          <option key={c.channel_id} value={c.channel_id}>
            {c.channel_name} ({c.channel_id})
          </option>
        ))}
      </select>
      <button type="button" className="channel-picker__toggle" onClick={() => setManualMode(true)}>
        Enter ID manually instead
      </button>
    </div>
  );
}
